import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { readAndCompressImage } from 'browser-image-resizer';
import heic2any from 'heic2any';
import Button from 'react-bootstrap/Button';

import { useDropzone } from 'react-dropzone';
import { useFormikContext } from 'formik';

import Swal from 'sweetalert2';

import { isEmpty, isNull } from 'lodash';
import { selectSectionsPerRepeatableSection } from '@redux/selectors/ListingSelector';
import { setUploadsCounter } from '@redux/modules/listing';
import { selectFormDefinition, selectStatusByName } from '@redux/selectors/FormSelector';

import usePhotoManager from '@hooks/usePhotoManager';
import apiService from '@services/apiService';

import GridList from './GridList';

const ImageDropzone = props => {
  const {
    photosPayload, // photosPayload is photos returned from FR and 'currentImages'
  } = props;

  const dispatch = useDispatch();
  const photoManager = usePhotoManager();
  const status = useSelector(state => selectStatusByName(state, 'Photos'));
  const formDefn = useSelector(selectFormDefinition);
  const [files, setFiles] = useState([]);

  const maxCount = useMemo(() => formDefn?.find(meta => meta.PageName === 'Photos')?.repeatableMaxCount ?? 25, [formDefn]);
  const sectionsPerRepeatableSection = useSelector(selectSectionsPerRepeatableSection);
  const photosCount = sectionsPerRepeatableSection.Photos.length;
  const { values } = useFormikContext();
  const formikPhotos = values?.Photos;

  useEffect(() => {
    if (photosPayload?.length) {
      photosPayload.forEach(payloadPhoto => {
        photoManager.addNewFlexRulePhoto(payloadPhoto);
      });
    }
  }, [photosPayload]);

  const isUploaded = keyName => Object.keys(formikPhotos ?? []).some(key => key === keyName);
  const duplicateImageFile = image => {
    let dupNo = 2;
    let newFileName = image.name.replace(/(\.[\w\d_-]+)$/i, `_${dupNo}$1`);
    while (isUploaded(newFileName)) {
      dupNo += 1;
      newFileName = image.name.replace(/(\.[\w\d_-]+)$/i, `_${dupNo}$1`);
    }
    return new File([image], newFileName, {
      type: image.type,
    });
  };

  const displayMaxCountExceedMessage = () => Swal.fire({
    position: 'center',
    title: 'Photos Exceeded',
    html: `The maximum number of photos must not exceed <strong class="text-danger">${maxCount}</strong>.`,
    icon: 'error',
    iconColor: 'var(--theme-danger)',
    showConfirmButton: false,
    timer: 5000,
  });

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/jpeg, image/png, image/jpg, .heic, .heif',
    maxFiles: maxCount,
    onDrop: async acceptedFiles => {
      dispatch(setUploadsCounter(acceptedFiles.length));

      if (photosCount + acceptedFiles.length > maxCount) {
        displayMaxCountExceedMessage();
        return;
      }

      await Promise.all(acceptedFiles.map(async (file, index) => {
        let image = file;
        const imagePath = image.path;
        let imageName = String(file.name);
        const imageType = isEmpty(file.type) || isNull(file.type) ? 'image/jpeg' : String(file.type);
        const imageOptions = {
          type: imageType,
        };
        const maxWidth = 1920;
        const maxHeight = 1024;
        const imageConfig = {
          maxWidth,
          maxHeight,
          mimeType: imageType,
          debug: true,
        };

        // Handle duplicate image
        if (isUploaded(imagePath)) {
          image = duplicateImageFile(file);
        }
        // Handle HEIC/HEIF image conversion
        if (image.name.toLowerCase().includes('.heic') || image.name.toLowerCase().includes('.heif')) {
          const convertedImageBlob = await heic2any({
            blob: image,
            quality: 1,
            toType: 'image/jpeg',
          });
          imageName = imageName.toLowerCase().includes('.heic') || imageName.toLowerCase().includes('.heif') ? `${imageName.substring(0, imageName.lastIndexOf('.'))}.jpg` : imageName;
          image = new File([convertedImageBlob], imageName, imageOptions);
        }

        // Handle image size
        if (image.width > maxWidth || image.height > maxHeight) {
          const aspectRatio = image.width / image.height;
          let targetWidth;
          let targetHeight;
          let quality;

          // Calculate target size
          if (image.width > maxWidth) {
            targetWidth = maxWidth;
            targetHeight = Math.floor(targetWidth / aspectRatio);
          } else {
            targetHeight = maxHeight;
            targetWidth = Math.floor(targetHeight * aspectRatio);
          }

          // Calculate quality based on target size
          const targetSize = targetWidth * targetHeight;
          if (targetSize < maxWidth * maxHeight) {
            quality = Math.floor((targetSize / (maxWidth * maxHeight)) * 100) / 100;
          } else {
            quality = 1;
          }

          // Compress image
          image = await readAndCompressImage(image, {
            ...imageConfig,
            maxWidth: targetWidth,
            maxHeight: targetHeight,
            quality,
          });
        } else { // Compress image whose scale is smaller than the target size
          image = await readAndCompressImage(image, imageConfig);
        }

        const imageFile = new File([image], imageName, imageOptions);
        photoManager.addNewDropzonePhoto(imageFile, index);

        return new Blob([imageFile], imageOptions);
      }));
    },
  });

  const handleDownloadAllPhotos = async e => {
    e.stopPropagation();
    try {
      const fileList = files.map(file => file.externalURL.split('/')[3]);
      const response = await apiService.Photos.getAllImages(fileList);
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      const listingId = values.ListingId;
      link.href = url;
      link.setAttribute('download', `Images for ${listingId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading images:', error);
    }
  };

  return (
    <div className="h-75">
      <br />
      <div {...getRootProps({ className: 'dropzoneContainer' })}>
        <input {...getInputProps()} />
        <p className="text-center mb-2">
          <i className="far fa-cloud-upload fa-2x" />
        </p>
        <p className="text-center text-muted mb-1">
          Drag &apos;n&apos; drop your photos here, or click to select them
          manually.
        </p>
        <p className="text-center fs-sm">
          Press &apos;Ctrl&apos; or ⌘ to multi-select
        </p>
        {values.ListingKeyNumeric !== '' && photosCount > 0 && (
          <div className="d-flex justify-content-center">
            <Button
              name="btnDownloadAllPhotos"
              variant="primary"
              type="button"
              size="sm"
              onClick={handleDownloadAllPhotos}
            >
              <i className="far fa-folder-download mr-2" aria-hidden="true" />
              Download All Photos
            </Button>
          </div>
        )}
        {photosCount === 0 && (
          <p className="text-center alert alert-info mt-2 mb-0 wobble-hor-bottom">
            <i className="far fa-exclamation-circle mr-2" />
            Please note that photos must be under 50 MB each in size.
            <br />
            <br />
            By uploading photos here, you agree to grant Pillar 9&trade; ownership of said photos, to do with as Pillar 9&trade; sees fit. You further represent that you have the right to grant Pillar 9&trade; such rights (check with your photographer).
          </p>
        )}
      </div>
      {status?.errorMessage && <div id="Photos" className="invalid-feedback">{status.errorMessage}</div>}
      <GridList
        files={files}
        setFiles={setFiles}
      />
    </div>
  );
};

ImageDropzone.propTypes = {
  photosPayload: PropTypes.arrayOf(PropTypes.shape()),
  onRemove: PropTypes.func,
  onChange: PropTypes.func,
};

export default ImageDropzone;
